/** Carried-comment ageing — W12.
 *
 * The headline is the GENERATION-WEIGHTED total, not the count, because twelve
 * first-carry comments are a busy week and one comment on its fourth carry is a
 * problem with a name on it. A register that reports only "724 carried" hides the
 * second inside the first.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import { Empty, Panel, Stat } from '@/components/nfa/NfaPrimitives';

const EdmCarriedAgeing = ({ projectId }) => {
  const T = useNfaTheme();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get(`/edm/projects/${projectId}/ageing/carried-comments`)
      .then((r) => setD(r.data)).catch(() => setD(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return <Empty T={T}><Loader2 className="w-4 h-4 animate-spin inline" /> Measuring…</Empty>;
  }
  if (!d) return <Empty T={T} testId="edm-ageing-unavailable">Ageing is unavailable.</Empty>;

  return (
    <div className="space-y-3" data-testid="edm-carried-ageing">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat T={T} label="Carried comments" value={d.carried}
          testId="edm-ageing-stat-carried" info={d.definition?.carried} />
        <Stat T={T} label="Generation-weighted" value={d.generationWeighted}
          tone={d.generationWeighted > d.carried ? 'amber' : undefined}
          testId="edm-ageing-stat-weighted" info={d.definition?.generationWeighted} />
        <Stat T={T} label={`Carried ${d.staleGenerationThreshold}× or more`} value={d.stale}
          tone={d.stale ? 'red' : undefined} testId="edm-ageing-stat-stale"
          info={d.definition?.generation} />
        <Stat T={T} label="Oldest" value={d.oldestDays != null ? `${d.oldestDays}d` : '—'}
          testId="edm-ageing-stat-oldest" info={d.definition?.age} />
      </div>

      <Panel T={T} title="Carried-comment ageing" subtitle={d.statement}
        testId="edm-ageing-panel">
        <div className="grid md:grid-cols-3 gap-4 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-[0.08em] mb-1"
              style={{ color: T.muted }}>By generation</div>
            {(d.byGeneration || []).map((g) => (
              <div key={g.generation} className="flex justify-between py-0.5"
                data-testid={`edm-ageing-gen-${g.generation}`}
                style={{ color: g.generation >= d.staleGenerationThreshold ? T.amber : T.body }}>
                <span>{g.label}</span><span>{g.count}</span>
              </div>
            ))}
            {!(d.byGeneration || []).length && (
              <span style={{ color: T.muted }}>nothing carried</span>
            )}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.08em] mb-1"
              style={{ color: T.muted }}>By age since first raised</div>
            {(d.byAge || []).map((b) => (
              <div key={b.bucket} className="flex justify-between py-0.5"
                data-testid={`edm-ageing-bucket-${b.bucket}`}>
                <span>{b.bucket}</span><span>{b.count}</span>
              </div>
            ))}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.08em] mb-1"
              style={{ color: T.muted }}>By discipline, weighted</div>
            {(d.byDiscipline || []).slice(0, 8).map((x) => (
              <div key={x.discipline} className="flex justify-between py-0.5"
                data-testid={`edm-ageing-disc-${x.discipline}`}>
                <span>{x.discipline}</span>
                <span>{x.weighted} <span style={{ color: T.muted }}>({x.carried})</span></span>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <Panel T={T} pad={false} title="The ones that have been pushed the most"
        subtitle="Each of these has survived a complete review cycle. The drawing was reissued around it."
        testId="edm-ageing-worst">
        {(d.worst || []).map((w) => (
          <div key={w.comment_id} className="px-4 py-2 text-[11.5px]"
            data-testid={`edm-ageing-worst-${w.comment_id}`}
            style={{ borderBottom: `1px solid ${T.border}` }}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono" style={{ color: T.primary }}>{w.document_no}</span>
              <span className="text-[9.5px] px-1.5 py-0.5 rounded"
                style={{ background: T.tint(T.amber), color: T.amber }}>
                carried {w.generation}×
              </span>
              <span className="text-[9.5px] px-1.5 py-0.5 rounded"
                style={{ background: T.surfaceAlt, color: T.body }}>{w.severity}</span>
              <span className="ml-auto text-[10px]" style={{ color: T.muted }}>
                {w.discipline} · {w.ageDays}d · now on Rev {w.nowOnRevision}
              </span>
            </div>
            <div className="mt-0.5" style={{ color: T.ink }}>{w.body}</div>
            <div className="text-[10px]" style={{ color: T.muted }}>{w.statement}</div>
          </div>
        ))}
        {!(d.worst || []).length && (
          <Empty T={T}>Nothing has been carried forward on this project.</Empty>
        )}
      </Panel>
    </div>
  );
};

export default EdmCarriedAgeing;
